<template>
    <div class="@container/detail flex flex-col h-full text-slate-800">
        <div class="px-8 pt-8 pb-7 border border-solid border-t-0 border-x-0 border-slate-100">
            <transition
                name="fade"
                mode="out-in"
                enterFromClass="opacity-0"
                enterToClass="opacity-100"
                leaveFromClass="opacity-100"
                leaveToClass="opacity-0"
            >
                <div v-if="loading" class="transition">
                    <div class="bg-slate-100 rounded-[8px] h-16 animate-pulse"></div>
                </div>
                <div v-else class="flex flex-col gap-3">
                    <div>
                        <vx-badge
                            v-if="!loading"
                            size="xs"
                            color="slate"
                            class="cursor-pointer"
                            @click="copyText(props.resource.resource_id)"
                        >
                            <copy-button label="resource ID" :value="props.resource.resource_id" />
                        </vx-badge>
                    </div>
                    <span class="font-extrabold text-xl leading-[1.2] transition break-words">
                        {{ props.resource.resource_name }}
                    </span>
                    <div v-if="isCorporate && resourceLink && !loading">
                        <router-button size="sm" :to="resourceLink" color="muted" class="w-full @md/detail:w-auto">
                            Go to Resource
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                        </router-button>
                    </div>
                </div>
            </transition>
        </div>

        <div class="px-8 pt-6 pb-8 flex flex-col gap-6">
            <div class="flex flex-col gap-2 @lg/detail:flex-row @lg/detail:flex-wrap">
                <template v-if="loading">
                    <div class="bg-slate-100 grow rounded-[8px] h-[52px] animate-pulse"></div>
                    <div class="bg-slate-100 grow rounded-[8px] h-[52px] animate-pulse"></div>
                    <div class="bg-slate-100 grow rounded-[8px] h-[52px] animate-pulse"></div>
                    <div class="bg-slate-100 grow rounded-[8px] h-[52px] animate-pulse"></div>
                </template>
                <template v-else>
                    <resource-detail-info>
                        <template v-slot:label>Sender</template>
                        <div class="flex items-center">
                            <font-awesome-icon
                                widthAuto
                                :icon="getSenderTypeIcon(props.resource.sender_type)"
                                class="mr-1"
                                :class="getSenderTypeColorClass(props.resource.sender_type)"
                            ></font-awesome-icon>
                            <span data-test="sender-name">
                                {{ resourceDetail?.sender?.name || senderTypeLabel(props.resource.sender_type) }}
                            </span>
                        </div>
                    </resource-detail-info>

                    <resource-detail-info>
                        <template v-slot:label>Resource Type</template>
                        <div class="flex items-center">
                            <font-awesome-icon
                                widthAuto
                                :icon="resourceTypeIcon(props.resource.resource_type)"
                                class="text-slate-400 mr-1"
                            ></font-awesome-icon>
                            <span>{{ resourceTypeLabel(props.resource.resource_type) }}</span>
                        </div>
                    </resource-detail-info>

                    <resource-detail-info v-if="resourceDetail?.audience_description">
                        <template v-slot:label>Audience</template>
                        {{ resourceDetail.audience_description }}
                    </resource-detail-info>

                    <resource-detail-info v-if="resourceDetail?.launched_at">
                        <template v-slot:label>Launched</template>
                        {{ dateTime(resourceDetail.launched_at) }}
                    </resource-detail-info>
                </template>
            </div>

            <div
                v-if="canHaveDetails"
                class="relative"
                :class="{
                    'min-h-60': loading,
                }"
            >
                <div
                    class="absolute inset-s-0 inset-bs-0 w-full bg-slate-100 rounded-[8px] transition pointer-events-none"
                    :class="{
                        'opacity-0 h-0': !loading,
                        'h-60': loading,
                    }"
                ></div>
                <transition
                    enterFromClass="opacity-0"
                    enterToClass="opacity-100"
                    leaveFromClass="opacity-100"
                    leaveToClass="opacity-0"
                >
                    <message-cloud
                        v-if="!loading && mainMessage"
                        class="transition relative"
                        fullWidth
                        :showDetails="false"
                        :message="mainMessage"
                    ></message-cloud>
                </transition>
                <span v-if="!loading && mainMessage" class="text-slate-400 text-xs">
                    Shows the {{ props.resource.resource_type === 'automation' ? 'last' : 'first' }} message only.
                    <template v-if="resourceLink && isCorporate">
                        Visit
                        <a
                            :href="resourceLink"
                            class="text-slate-400 underline!"
                            @click="navigate(resourceLink, $event)"
                            >resource</a
                        >
                        to see all.
                    </template>
                </span>
            </div>
        </div>

        <div
            class="border border-solid border-b-0 border-x-0 border-slate-200 mt-auto"
            :class="{
                'animate-pulse': loading,
            }"
        >
            <resource-detail-metric
                v-for="metric in displayedMetrics"
                :key="metric.value"
                :target="targets?.[props.category]?.[metric.value]"
                :metric="metric.value"
                :activeMetric="activeMetric"
                :resourceMetric="resource[metric.value]"
                :loading="loading"
            ></resource-detail-metric>
        </div>
    </div>
</template>

<script setup>
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { VxBadge } from '@voxie/frontend-components';
import { computed, onMounted } from 'vue';
import { useCopyText } from '~/composables/useCopyText';
import { useRouterNavigation } from '~/composables/useRouterNavigation';
import {
    getSenderTypeIcon,
    getSenderTypeColorClass,
    metricsOptions,
    resourceTypeIcon,
    resourceTypeLabel,
} from '~/utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CopyButton from '~/components/general/CopyButton.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import ResourceDetailInfo from '~/components/analytics/partial/resource-detail/ResourceDetailInfo.vue';
import ResourceDetailMetric from './ResourceDetailMetric.vue';
import { analyticsCategoriesValues } from '~/constants/categories';
import { useResourcesDetails } from '~/components/analytics/useResourcesDetails';
import { dateTime } from '~/utils/helpers';
import { useAnalyticsTargets } from '../../useAnalyticsTargets';
import { SENDER_TYPE_CORPORATE, senderTypeLabel } from '~/constants/senderTypes';
import { useCostVisibility } from '~/composables/useCostVisibility';

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
    category: {
        type: String,
        required: false,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    activeMetric: {
        type: String,
        required: false,
        validator: (value) => metricsOptions.some((metric) => metric.value === value),
    },
});

const { copyText } = useCopyText();
const { navigate } = useRouterNavigation();
const { getResourceDetail, loading: loadingResourceDetails } = useResourcesDetails();
const { targets, fetchTargets, loading: loadingTargets } = useAnalyticsTargets();
const { showEstimatedCost } = useCostVisibility();

const displayedMetrics = computed(() => {
    if (showEstimatedCost.value) {
        return metricsOptions;
    }
    return metricsOptions.filter((metric) => metric.value !== 'messaging_cost');
});

const isCorporate = computed(() => props.resource.sender_type === SENDER_TYPE_CORPORATE);
const canHaveDetails = computed(() =>
    ['one_time_campaign', 'evergreen_campaign', 'automation'].includes(props.resource.resource_type)
);

const resourceLink = computed(() => {
    if (!isCorporate.value) {
        return undefined;
    }

    if (props.resource.resource_type === 'one_time_campaign' || props.resource.resource_type === 'evergreen_campaign') {
        return `/messaging/campaigns/${props.resource.resource_id}`;
    } else if (props.resource.resource_type === 'automation') {
        return `/automation/event/${props.resource.resource_id}`;
    } else if (props.resource.resource_type === 'user') {
        return `/contacts/${props.resource.resource_id}/view`;
    }

    return undefined;
});

const resourceDetail = computed(() => getResourceDetail(props.resource.resource_type, props.resource.resource_id));

const loading = computed(
    () => (canHaveDetails.value && loadingResourceDetails.value && !resourceDetail.value) || loadingTargets.value
);
const mainMessage = computed(() => resourceDetail.value?.main_message);

onMounted(() => {
    fetchTargets(Spark.state.currentTeam.id);
});
</script>
